// Tela "Assinatura" (só o titular): a situação da assinatura, assinar (mensal
// ou anual), as faturas com o botão de pagar, cancelar e desfazer o
// cancelamento. Precisa de internet: o pagamento é feito no Asaas.
import { LANCAMENTO, MESES_ATE_APAGAR, RESPONSAVEL } from '../config.js';
import {
  minhaAssinatura, opcoesDeAssinatura, pagoAte, pedirAoServidor, resumoDaAssinatura, SITUACAO_DA_FATURA, terminaSeCancelar,
  textoDosCaminhoes, tituloDaFatura,
} from '../lib/cobranca.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, plural } from '../lib/formato.js';
import { precoPorMes } from '../lib/preco.js';
import { nestaTela } from '../rotas.js';
import { atualizarEmpresa, ehTitular, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, campo, carregando, confirmar, emblema, faixa, formulario, secao } from '../ui/componentes.js';

export function telaCobranca() {
  if (!ehTitular()) {
    return casca({ titulo: 'Assinatura', voltar: '/conta', conteudo: [faixa('Só o titular da conta vê e mexe na assinatura.', 'info')] });
  }
  const tela = nestaTela();
  const corpo = h('div', { class: 'lista' }, carregando());
  const empresa = sessao.empresa;

  (async () => {
    let info;
    try {
      info = await minhaAssinatura(empresa.id);
    } catch (erro) {
      if (tela.ainda()) trocar(corpo, faixa(mensagemDeErro(erro), 'perigo', 'Não deu para abrir a assinatura'));
      return;
    }
    if (!tela.ainda()) return;
    // A situação da conta (só consulta, carência) fica igual à do servidor.
    if (sessao.empresa?.id === empresa.id) atualizarEmpresa({ ...sessao.empresa, ...info.empresa });
    trocar(corpo, ...montar(info, tela));
  })();

  return casca({ titulo: 'Assinatura', voltar: '/conta', conteudo: [corpo] });
}

function montar(info, tela) {
  const resumo = resumoDaAssinatura(info);
  return [
    faixa(resumo.texto, resumo.tom, resumo.titulo),
    resumo.desfazer && botaoDeDesfazer(tela),
    resumo.assinar && secao('Assinar', blocoDeAssinar(info, tela)),
    secao('Faturas', listaDeFaturas(info)),
    resumo.cancelar && botaoDeCancelar(info, tela),
    h('p', { class: 'pequeno' },
      'Pix, boleto ou cartão: você escolhe na hora de pagar, na página do Asaas. Dúvidas: ',
      h('a', { href: `mailto:${RESPONSAVEL.email}` }, RESPONSAVEL.email), '.'),
  ];
}

function blocoDeAssinar(info, tela) {
  if (info.caminhoes === 0) {
    return h('div', { class: 'lista' },
      faixa('A assinatura é por caminhão com placa ativa. Ative a placa de pelo menos um caminhão para assinar.', 'alerta'),
      h('a', { class: 'botao secundario cheio', href: '#/veiculos' }, 'Ir para Veículos'));
  }
  const e = info.empresa;
  const primeira = e.situacao === 'teste' && e.teste_ate >= info.hoje
    ? `A primeira fatura vence em ${data(diaSeguinte(e.teste_ate))}, no dia seguinte ao fim do teste.`
    : 'A primeira fatura vence hoje.';
  return formulario({
    filhos: [
      h('p', null, textoDosCaminhoes(info.caminhoes)),
      campo({
        rotulo: 'Como prefere pagar',
        nome: 'ciclo',
        tipo: 'segmentos',
        valor: 'mensal',
        opcoes: opcoesDeAssinatura(info).map((o) => [o.valor, o.rotulo, o.preco]),
      }),
      h('p', { class: 'pequeno' },
        info.previa.lancamento
          ? `Preço de lançamento: ${precoPorMes({ lancamento: true })}, nos primeiros ${LANCAMENTO.meses} meses. Depois, ${precoPorMes()}.`
          : `Preço: ${precoPorMes()}.`),
      info.previa.lancamento && info.lugares?.mostrar
        && h('p', { class: 'pequeno' }, `Faltam ${plural(info.lugares.restam, 'lugar', 'lugares')} com o preço de lançamento.`),
      h('p', { class: 'pequeno' }, `${primeira} Caminhão incluído depois entra proporcional aos dias. Ao assinar, você concorda com os `,
        h('a', { href: '#/termos' }, 'Termos de uso'), '.'),
    ],
    textoBotao: 'Assinar',
    textoEnviando: 'Assinando…',
    aoEnviar: async (d) => {
      const r = await pedirAoServidor('assinar', { empresa: sessao.empresa.id, ciclo: d.ciclo });
      avisar(r.aviso ?? (r.link ? 'Assinatura feita. A fatura está na lista, com o botão "Pagar".' : 'Assinatura feita.'));
      tela.renderizar();
    },
  });
}

// "2026-10-31" → "2026-11-01"
function diaSeguinte(dia) {
  const [a, m, d] = dia.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, d + 1)).toISOString().slice(0, 10);
}

function listaDeFaturas(info) {
  const faturas = info.faturas ?? [];
  if (faturas.length === 0) return h('p', { class: 'pequeno' }, 'Nenhuma fatura ainda.');
  return h('div', { class: 'lista' }, faturas.map((f) => {
    const [rotulo, tom] = SITUACAO_DA_FATURA[f.situacao] ?? [f.situacao, ''];
    const pagar = ['aberta', 'vencida'].includes(f.situacao) && f.link;
    return h('div', { class: 'cartao fatura', 'data-fatura': f.id },
      h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, tituloDaFatura(f)),
        h('span', { class: 'cartao-linha' }, `${dinheiro(f.valor)} · vence em ${data(f.vencimento)}`),
        f.situacao === 'paga' && f.pago_em && h('span', { class: 'cartao-linha' }, `Paga em ${data(f.pago_em)}`),
        h('span', { class: 'cartao-emblemas' }, emblema(rotulo, tom)),
        (f.itens ?? []).length > 1 && h('details', { class: 'mais-detalhes' },
          h('summary', null, 'Ver o que está na fatura'),
          h('ul', null, f.itens.map((i) => h('li', null, `${i.descricao}: ${dinheiro(i.valor)}`)))),
        pagar && h('a', { class: 'botao cheio', href: f.link, target: '_blank', rel: 'noopener' }, 'Pagar')));
  }));
}

function botaoDeCancelar(info, tela) {
  const botao = h('button', { class: 'botao perigo cheio', type: 'button' }, 'Cancelar a assinatura');
  botao.addEventListener('click', async () => {
    const ate = terminaSeCancelar(info);
    const e = info.empresa;
    let texto;
    if (!pagoAte(info)) {
      texto = 'Nenhuma fatura desta assinatura foi paga: ela termina hoje e a fatura em aberto é cancelada.';
      if (e.situacao === 'teste' && e.teste_ate >= info.hoje) texto += ` O teste grátis continua até ${data(e.teste_ate)}.`;
    } else if (ate > info.hoje) {
      texto = `A conta continua funcionando até ${data(ate)}, o fim do período pago. Depois, fica só para consulta, e os dados são apagados ${MESES_ATE_APAGAR} meses depois (avisamos antes). Até lá, dá para desfazer.`;
    } else {
      texto = `O período pago já terminou: a assinatura termina hoje, e a fatura em aberto é cancelada. Depois, a conta fica só para consulta, e os dados são apagados ${MESES_ATE_APAGAR} meses depois (avisamos antes).`;
    }
    if (!(await confirmar({ titulo: 'Cancelar a assinatura?', texto, sim: 'Cancelar a assinatura', nao: 'Voltar', perigo: true }))) return;
    botao.disabled = true;
    botao.textContent = 'Cancelando…';
    try {
      await pedirAoServidor('cancelar', { empresa: sessao.empresa.id });
      avisar('Assinatura cancelada.');
    } catch (erro) {
      avisar(erro.paraMostrar ?? mensagemDeErro(erro), 'erro');
    }
    tela.renderizar();
  });
  return h('div', { class: 'acoes' }, botao);
}

function botaoDeDesfazer(tela) {
  const botao = h('button', { class: 'botao cheio', type: 'button' }, 'Desfazer o cancelamento');
  botao.addEventListener('click', async () => {
    botao.disabled = true;
    botao.textContent = 'Desfazendo…';
    try {
      await pedirAoServidor('desfazer', { empresa: sessao.empresa.id });
      avisar('A assinatura continua.');
    } catch (erro) {
      avisar(erro.paraMostrar ?? mensagemDeErro(erro), 'erro');
    }
    tela.renderizar();
  });
  return h('div', { class: 'acoes' }, botao);
}
